'use client'

import { useEffect, useRef, useState } from 'react'

import { DocSearchSelect } from './DocSearchSelect'

function applicationStatusColor(status) {
  if (status === 'approved') return 'var(--theme-success-500, #2e7d32)'
  if (status === 'declined') return 'var(--theme-error-500, #c62828)'
  return 'var(--theme-elevation-500)'
}

function agreementStatusColor(status) {
  if (status === 'signed') return 'var(--theme-success-500, #2e7d32)'
  if (status === 'void') return 'var(--theme-error-500, #c62828)'
  return 'var(--theme-elevation-500)'
}

// The empty-query listing surfaces recent approved applications — the set
// you actually generate offers for. Typing searches every status.
const APPROVED_FIRST = { 'where[status][equals]': 'approved' }

// The three ways to start a draft offer. Application is the original
// Zero-to-Hero flow (committee-approved application); Deal and Manual are
// the website-build paths — a CRM deal (contact-submissions) or a
// hand-typed founder with no CRM record yet. Each tab's program drives
// which offer templates are offered below.
const SOURCE_TABS = [
  { key: 'application', label: 'Application', program: 'zero-to-hero' },
  { key: 'deal', label: 'Deal', program: 'website-build' },
  { key: 'manual', label: 'Manual', program: 'website-build' },
]
const PROGRAM_LABEL = { 'zero-to-hero': 'Zero-to-Hero', 'website-build': 'Website Build' }

function usd(n) {
  return `$${Number(n || 0).toLocaleString()}`
}

function humanize(value) {
  return value ? String(value).replace(/-/g, ' ') : ''
}

function dealLabel(deal) {
  const who = deal.name || deal.email || `#${deal.id}`
  const bits = [who]
  if (deal.offering) bits.push(humanize(deal.offering))
  if (typeof deal.dealValue === 'number') bits.push(usd(deal.dealValue))
  return bits.join(' • ')
}

// Deal-pipeline search: open deals only (not won/lost — those are settled),
// excluding zero-to-hero (that program's deals are worked from the
// Application tab, via z2h-applications, not contact-submissions), matching
// name/email/company, newest first. `offering` is optional on
// contact-submissions, and Payload's not_equals treats a NULL offering as
// distinct from 'zero-to-hero' (i.e. it's INCLUDED) — intentional: an
// unclassified open deal is still a legitimate website-build proposal
// target, and its offering falls back to 'revenue-engine' at sign time.
function buildDealQuery(q) {
  const params = new URLSearchParams({ limit: '8', depth: '0', sort: '-createdAt' })
  params.set('where[and][0][status][not_in][0]', 'won')
  params.set('where[and][0][status][not_in][1]', 'lost')
  params.set('where[and][1][offering][not_equals]', 'zero-to-hero')
  const trimmed = q.trim()
  if (trimmed) {
    const fields = ['name', 'email', 'company']
    fields.forEach((f, i) => {
      params.set(`where[and][2][or][${i}][${f}][like]`, trimmed)
    })
  }
  return params.toString()
}

// Renders above the Z2H Agreements list. Generates a draft offer for an
// approved application, a CRM deal, or a hand-typed manual entry: template
// economics + AI-suggested terms. The committee then tunes the draft and
// sets it to Sent to email the founder.
export function GenerateOfferPanel() {
  const [source, setSource] = useState('application')
  const [applicationId, setApplicationId] = useState('')
  const [dealId, setDealId] = useState('')
  const [manual, setManual] = useState({ name: '', email: '', business: '' })
  const [manualError, setManualError] = useState(null)
  const [templateId, setTemplateId] = useState('')
  const [templates, setTemplates] = useState([])
  const [templatesLoading, setTemplatesLoading] = useState(true)
  // Distinct from "genuinely zero templates for this program" — a fetch
  // failure (network blip, 5xx) must not be mistaken for the create-one
  // empty state, and must offer a retry instead of silently blocking
  // Generate with misleading copy.
  const [templatesError, setTemplatesError] = useState(false)
  const [templatesReloadTick, setTemplatesReloadTick] = useState(0)
  const [busy, setBusy] = useState(false)
  const [result, setResult] = useState(null)

  const activeTab = SOURCE_TABS.find((t) => t.key === source) || SOURCE_TABS[0]
  const program = activeTab.program

  // Template select tracks the active tab's program — Application draws
  // from Zero-to-Hero templates, Deal/Manual from Website Build templates.
  // Switching between Deal and Manual (same program) reuses the already-
  // fetched list rather than refetching. templatesReloadTick lets the
  // retry affordance re-run this same effect after a fetch error.
  useEffect(() => {
    let cancelled = false
    setTemplatesLoading(true)
    setTemplatesError(false)
    fetch(
      `/payload-api/z2h-offer-templates?where[program][equals]=${program}&limit=100&depth=0&sort=name`,
      { credentials: 'include' },
    )
      .then((res) => res.json())
      .then((data) => {
        if (cancelled) return
        const docs = Array.isArray(data.docs) ? data.docs : []
        setTemplates(docs)
        setTemplateId(docs[0] ? String(docs[0].id) : '')
      })
      .catch(() => {
        if (!cancelled) {
          setTemplates([])
          setTemplateId('')
          setTemplatesError(true)
        }
      })
      .finally(() => {
        if (!cancelled) setTemplatesLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [program, templatesReloadTick])

  // Switching tabs must fully clear the OTHER tabs' selection state — not
  // just the visible search-box display. applicationId/dealId are held here
  // in the parent and are what generate() actually posts; DocSearchSelect/
  // DealSearchSelect unmount when their tab isn't active (conditionally
  // rendered below), so a stale non-empty id would silently survive a
  // round trip through another tab and get posted on the next Generate even
  // though the search box shows empty on return. Clearing here also feeds
  // the search components' own `if (!value)` reset effect a genuinely
  // falsy value, so a REMOUNTED instance (or one that didn't fully unmount)
  // still resolves to a clear display.
  function switchSource(key) {
    setSource(key)
    setApplicationId('')
    setDealId('')
    setManual({ name: '', email: '', business: '' })
    setManualError(null)
    setResult(null)
  }

  async function generate(e) {
    e.preventDefault()
    if (busy) return
    setManualError(null)

    let body
    if (source === 'application') {
      if (!applicationId.trim()) return
      body = { applicationId: applicationId.trim() }
    } else if (source === 'deal') {
      if (!dealId) return
      body = { dealId }
    } else {
      const name = manual.name.trim()
      const email = manual.email.trim()
      const business = manual.business.trim()
      if (!name || !email || !business) {
        setManualError('Name, email, and business are all required.')
        return
      }
      body = { manual: { name, email, business } }
    }
    if (templateId) body.templateId = templateId

    setBusy(true)
    setResult(null)
    try {
      const res = await fetch('/api/z2h/agreements/generate', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      })
      const data = await res.json()
      if (!res.ok || !data.ok) throw new Error(data.error || `HTTP ${res.status}`)
      setResult({
        ok: true,
        message: data.created
          ? `Draft agreement #${data.agreementId} generated — upfront ${usd(data.terms?.upfrontAmount)}, equity ${data.terms?.equityPct}%, build ${usd(data.terms?.buildCost)}. Review it, tune the terms, then set status to Sent.`
          : `That source already has agreement #${data.agreementId} — edit that one instead.`,
        agreementId: data.agreementId,
      })
      if (source === 'application') setApplicationId('')
      else if (source === 'deal') setDealId('')
      else setManual({ name: '', email: '', business: '' })
    } catch (err) {
      setResult({ ok: false, message: err.message })
    } finally {
      setBusy(false)
    }
  }

  const field = {
    padding: '10px 14px',
    borderRadius: 8,
    border: '1px solid var(--theme-elevation-150)',
    background: 'var(--theme-input-bg)',
    color: 'var(--theme-elevation-800)',
    fontSize: 14,
    minWidth: 0,
  }
  // 44px touch targets for the new tab/deal/manual/template controls — the
  // Application tab's DocSearchSelect keeps `field` as-is (today's behavior
  // unchanged), everything new sizes up from it.
  const touchField = { ...field, minHeight: 44, boxSizing: 'border-box' }
  const manualField = { ...touchField, fontSize: 16 }

  const canSubmit =
    !busy &&
    templates.length > 0 &&
    (source === 'application'
      ? Boolean(applicationId.trim())
      : source === 'deal'
        ? Boolean(dealId)
        : Boolean(manual.name.trim() && manual.email.trim() && manual.business.trim()))

  return (
    <div
      style={{
        border: '1px solid var(--theme-elevation-150)',
        borderRadius: 12,
        padding: '20px 24px',
        marginBottom: 24,
        background: 'var(--theme-elevation-50)',
      }}
    >
      <h3 style={{ margin: 0, fontSize: 15 }}>Generate an offer</h3>
      <p style={{ margin: '6px 0 14px', fontSize: 13, color: 'var(--theme-elevation-500)' }}>
        Creates a draft agreement from an approved application, a CRM deal,
        or a hand-typed founder — template economics plus AI-suggested terms.
        You tune the numbers, then set status to Sent to email the founder.
      </p>

      <div role="tablist" aria-label="Offer source" style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
        {SOURCE_TABS.map((t) => (
          <button
            key={t.key}
            type="button"
            role="tab"
            aria-selected={source === t.key}
            onClick={() => switchSource(t.key)}
            style={{
              minHeight: 44,
              padding: '0 18px',
              borderRadius: 8,
              border:
                source === t.key
                  ? '1px solid var(--theme-elevation-800)'
                  : '1px solid var(--theme-elevation-150)',
              background: source === t.key ? 'var(--theme-elevation-800)' : 'var(--theme-elevation-0)',
              color: source === t.key ? 'var(--theme-elevation-0)' : 'var(--theme-text)',
              fontSize: 13,
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            {t.label}
          </button>
        ))}
      </div>

      <form onSubmit={generate} style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'flex-start' }}>
        {source === 'application' && (
          <DocSearchSelect
            collection="z2h-applications"
            placeholder="Search applications — founder, venture, or email…"
            value={applicationId}
            onChange={(id) => setApplicationId(id)}
            initialWhere={APPROVED_FIRST}
            statusColor={applicationStatusColor}
            fieldStyle={field}
          />
        )}

        {source === 'deal' && (
          <DealSearchSelect value={dealId} onChange={(id) => setDealId(id)} fieldStyle={touchField} />
        )}

        {source === 'manual' && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, flex: '1 1 100%' }}>
            <input
              type="text"
              placeholder="Name"
              aria-label="Founder or contact name"
              value={manual.name}
              onChange={(e) => setManual((m) => ({ ...m, name: e.target.value }))}
              style={{ ...manualField, flex: '1 1 180px' }}
            />
            <input
              type="email"
              placeholder="Email"
              aria-label="Email"
              value={manual.email}
              onChange={(e) => setManual((m) => ({ ...m, email: e.target.value }))}
              style={{ ...manualField, flex: '1 1 200px' }}
            />
            <input
              type="text"
              placeholder="Business"
              aria-label="Business name"
              value={manual.business}
              onChange={(e) => setManual((m) => ({ ...m, business: e.target.value }))}
              style={{ ...manualField, flex: '1 1 200px' }}
            />
          </div>
        )}

        <select
          value={templateId}
          onChange={(e) => setTemplateId(e.target.value)}
          disabled={templatesLoading || templates.length === 0}
          aria-label="Offer template"
          style={{ ...touchField, flex: '0 1 220px' }}
        >
          {templates.length === 0 ? (
            <option value="">{templatesLoading ? 'Loading templates…' : 'No templates'}</option>
          ) : (
            templates.map((t) => (
              <option key={t.id} value={String(t.id)}>
                {t.name}
              </option>
            ))
          )}
        </select>

        <button
          type="submit"
          disabled={!canSubmit}
          style={{
            minHeight: 44,
            padding: '0 22px',
            borderRadius: 8,
            border: 'none',
            background: 'var(--theme-elevation-800)',
            color: 'var(--theme-elevation-0)',
            fontSize: 14,
            fontWeight: 600,
            cursor: busy ? 'wait' : 'pointer',
            opacity: canSubmit ? 1 : 0.6,
          }}
        >
          {busy ? 'Generating (AI terms)…' : 'Generate offer'}
        </button>
      </form>

      {manualError && (
        <p style={{ margin: '10px 0 0', fontSize: 13, color: 'var(--theme-error-500, #c62828)' }}>
          {manualError}
        </p>
      )}

      {!templatesLoading && templatesError && (
        <p style={{ margin: '12px 0 0', fontSize: 13, color: 'var(--theme-error-500, #c62828)' }}>
          Couldn&apos;t load templates —{' '}
          <button
            type="button"
            onClick={() => setTemplatesReloadTick((t) => t + 1)}
            style={{
              border: 'none',
              background: 'none',
              padding: 0,
              color: 'inherit',
              textDecoration: 'underline',
              cursor: 'pointer',
              fontSize: 13,
            }}
          >
            retry
          </button>
        </p>
      )}

      {!templatesLoading && !templatesError && templates.length === 0 && (
        <div
          style={{
            marginTop: 12,
            padding: '10px 14px',
            borderRadius: 8,
            border: '1px solid var(--theme-warning-400, #c98a1b)',
            background: 'var(--theme-warning-50, #fdf4e3)',
            fontSize: 13,
          }}
        >
          {`No ${PROGRAM_LABEL[program]} templates yet — create one in Offer Templates first`}
        </div>
      )}

      {result && (
        <p
          style={{
            margin: '12px 0 0',
            fontSize: 13,
            color: result.ok
              ? 'var(--theme-success-500, #2e7d32)'
              : 'var(--theme-error-500, #c62828)',
          }}
        >
          {result.message}{' '}
          {result.ok && result.agreementId ? (
            <a href={`/admin/collections/z2h-agreements/${result.agreementId}`}>
              Open agreement
            </a>
          ) : null}
        </p>
      )}
      <SigningLinkTool field={field} />
    </div>
  )
}

// Search widget for the Deal tab. Mirrors DocSearchSelect's shape (search →
// debounced fetch → dropdown → select-to-id) but targets contact-submissions
// with its own fields and WHERE clause (open, non-zero-to-hero deals) and
// its own result-row label (name • offering • dealValue) — DocSearchSelect's
// SEARCH_FIELDS/docLabel are hardcoded to the z2h-applications shape
// (founderName/ventureName), which doesn't fit a deal record.
function DealSearchSelect({ value, onChange, fieldStyle }) {
  const [query, setQuery] = useState('')
  const [selected, setSelected] = useState(null)
  const [options, setOptions] = useState([])
  const [open, setOpen] = useState(false)
  const [highlight, setHighlight] = useState(0)
  const [loading, setLoading] = useState(false)
  const rootRef = useRef(null)
  const fetchSeq = useRef(0)

  useEffect(() => {
    if (!value) {
      setSelected(null)
      setQuery('')
    }
  }, [value])

  useEffect(() => {
    if (!open) return
    const seq = ++fetchSeq.current
    setLoading(true)
    const t = setTimeout(async () => {
      try {
        const res = await fetch(`/payload-api/contact-submissions?${buildDealQuery(query)}`, {
          credentials: 'include',
        })
        const data = await res.json()
        if (seq === fetchSeq.current) {
          setOptions(Array.isArray(data.docs) ? data.docs : [])
          setHighlight(0)
        }
      } catch {
        if (seq === fetchSeq.current) setOptions([])
      } finally {
        if (seq === fetchSeq.current) setLoading(false)
      }
    }, query.trim() ? 250 : 0)
    return () => clearTimeout(t)
  }, [query, open])

  useEffect(() => {
    function onDocMouseDown(e) {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false)
    }
    document.addEventListener('mousedown', onDocMouseDown)
    return () => document.removeEventListener('mousedown', onDocMouseDown)
  }, [])

  function select(deal) {
    setSelected(deal)
    setQuery('')
    setOpen(false)
    onChange(String(deal.id), deal)
  }

  function clear() {
    setSelected(null)
    setQuery('')
    onChange('', null)
  }

  function onKeyDown(e) {
    if (!open) return
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setHighlight((h) => Math.min(h + 1, options.length - 1))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setHighlight((h) => Math.max(h - 1, 0))
    } else if (e.key === 'Enter') {
      e.preventDefault()
      if (options[highlight]) select(options[highlight])
    } else if (e.key === 'Escape') {
      setOpen(false)
    }
  }

  return (
    <div ref={rootRef} style={{ position: 'relative', flex: '1 1 260px', minWidth: 0 }}>
      {selected ? (
        <div
          style={{
            ...fieldStyle,
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            width: '100%',
            boxSizing: 'border-box',
          }}
        >
          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {dealLabel(selected)}{' '}
            <span style={{ color: 'var(--theme-elevation-500)' }}>#{selected.id}</span>
          </span>
          <button
            type="button"
            onClick={clear}
            aria-label="Clear selection"
            style={{
              marginLeft: 'auto',
              border: 'none',
              background: 'none',
              color: 'var(--theme-elevation-500)',
              cursor: 'pointer',
              fontSize: 15,
              lineHeight: 1,
              padding: 2,
            }}
          >
            ×
          </button>
        </div>
      ) : (
        <input
          type="text"
          role="combobox"
          aria-expanded={open}
          aria-autocomplete="list"
          placeholder="Search deals — name, email, or company…"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value)
            setOpen(true)
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={onKeyDown}
          style={{ ...fieldStyle, width: '100%', boxSizing: 'border-box' }}
        />
      )}
      {open && !selected && (
        <ul
          role="listbox"
          style={{
            position: 'absolute',
            top: 'calc(100% + 4px)',
            left: 0,
            right: 0,
            zIndex: 20,
            margin: 0,
            padding: 4,
            listStyle: 'none',
            maxHeight: 280,
            overflowY: 'auto',
            borderRadius: 8,
            border: '1px solid var(--theme-elevation-150)',
            background: 'var(--theme-input-bg, var(--theme-elevation-0))',
            boxShadow: '0 8px 24px rgba(0,0,0,.12)',
          }}
        >
          {loading && options.length === 0 ? (
            <li style={{ padding: '10px 12px', fontSize: 13, color: 'var(--theme-elevation-500)' }}>
              Searching…
            </li>
          ) : options.length === 0 ? (
            <li style={{ padding: '10px 12px', fontSize: 13, color: 'var(--theme-elevation-500)' }}>
              {query.trim() ? 'No matches.' : 'No open deals yet.'}
            </li>
          ) : (
            options.map((deal, i) => (
              <li key={deal.id} role="option" aria-selected={i === highlight}>
                <button
                  type="button"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => select(deal)}
                  onMouseEnter={() => setHighlight(i)}
                  style={{
                    display: 'flex',
                    alignItems: 'baseline',
                    gap: 8,
                    width: '100%',
                    textAlign: 'left',
                    padding: '8px 10px',
                    border: 'none',
                    borderRadius: 6,
                    cursor: 'pointer',
                    fontSize: 13,
                    color: 'var(--theme-text)',
                    background: i === highlight ? 'var(--theme-elevation-100)' : 'transparent',
                  }}
                >
                  <span
                    style={{
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {dealLabel(deal)}
                  </span>
                  <span style={{ marginLeft: 'auto', flexShrink: 0, fontSize: 12, color: 'var(--theme-elevation-500)' }}>
                    #{deal.id}
                  </span>
                </button>
              </li>
            ))
          )}
        </ul>
      )}
    </div>
  )
}

// Send agreements through any channel: fetch an agreement's private signing
// link and copy it. Setting status to Sent still emails the founder — this is
// the manual alternative (text, DM, personal email) using the same token.
// The link path branches on the agreement's program: Zero-to-Hero keeps its
// original founder-facing page, website-build agreements go to the neutral
// offer page.
function SigningLinkTool({ field }) {
  const [agreementId, setAgreementId] = useState('')
  const [busy, setBusy] = useState(false)
  const [link, setLink] = useState(null)
  const [error, setError] = useState(null)
  const [copied, setCopied] = useState(false)

  async function fetchLink(e) {
    e.preventDefault()
    if (!agreementId.trim() || busy) return
    setBusy(true)
    setError(null)
    setLink(null)
    setCopied(false)
    try {
      const res = await fetch(
        `/payload-api/z2h-agreements/${agreementId.trim()}?depth=0`,
        { credentials: 'include' },
      )
      if (!res.ok) throw new Error(`Agreement #${agreementId.trim()} not found`)
      const doc = await res.json()
      if (!doc.offerToken) throw new Error('This agreement has no offer token yet — save it once first.')
      const path =
        doc.program === 'website-build'
          ? `/offer/${doc.offerToken}`
          : `/zero-to-hero/offer/${doc.offerToken}`
      const url = `${window.location.origin}${path}`
      setLink({ url, status: doc.status, founder: doc.founderName || doc.email || '' })
      try {
        await navigator.clipboard.writeText(url)
        setCopied(true)
      } catch {
        /* clipboard blocked — the link is still shown for manual copy */
      }
    } catch (err) {
      setError(err.message)
    } finally {
      setBusy(false)
    }
  }

  return (
    <div
      style={{
        marginTop: 18,
        paddingTop: 14,
        borderTop: '1px solid var(--theme-elevation-150)',
      }}
    >
      <h4 style={{ margin: 0, fontSize: 13 }}>Copy signing link (send manually)</h4>
      <p style={{ margin: '4px 0 10px', fontSize: 12, color: 'var(--theme-elevation-500)' }}>
        Grab the agreement&apos;s private signing URL to send through any
        channel. Remember to set the agreement&apos;s status to Sent so the
        pipeline tracks it (that also emails the founder).
      </p>
      <form onSubmit={fetchLink} style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
        <DocSearchSelect
          collection="z2h-agreements"
          placeholder="Search agreements — founder, venture, or email…"
          value={agreementId}
          onChange={(id) => setAgreementId(id)}
          statusColor={agreementStatusColor}
          fieldStyle={field}
        />
        <button
          type="submit"
          disabled={busy || !agreementId.trim()}
          style={{
            padding: '10px 18px',
            borderRadius: 8,
            border: '1px solid var(--theme-elevation-250)',
            background: 'var(--theme-elevation-100)',
            color: 'var(--theme-text)',
            fontSize: 13,
            cursor: busy ? 'wait' : 'pointer',
            opacity: busy || !agreementId.trim() ? 0.6 : 1,
          }}
        >
          {busy ? 'Fetching…' : copied ? 'Copied!' : 'Copy signing link'}
        </button>
      </form>
      {link && (
        <p style={{ margin: '10px 0 0', fontSize: 12, wordBreak: 'break-all' }}>
          {copied ? '✓ Copied to clipboard — ' : ''}
          <code>{link.url}</code>{' '}
          <span style={{ color: 'var(--theme-elevation-500)' }}>
            ({link.founder} · status: {link.status})
          </span>
        </p>
      )}
      {error && (
        <p style={{ margin: '10px 0 0', fontSize: 12, color: 'var(--theme-error-500, #c62828)' }}>
          {error}
        </p>
      )}
    </div>
  )
}
